/* eslint-disable no-shadow,no-param-reassign,no-await-in-loop */
import { PDFDocument } from 'pdf-lib';
import { cloneDeep, uniqWith, isEqual } from 'lodash';
import { getUnsplitAttachments, getAttachmentByUrl, downloadFile } from '../../services/api';

const state = {
  separatedDocuments: [],
  attachmentList: [],
  splittedAttachment: null,
  numberOfPages: 0,
  pagination_splitting: {
    count: 0,
    current: null,
    next: null,
    previous: null,
  },
};

const getters = {
  separatedDocuments: (state) => state.separatedDocuments,
  attachmentList: (state) => state.attachmentList,
  splittedAttachment: (state) => state.splittedAttachment,
  pagination_splitting: (state) => state.pagination_splitting,
};

const mutations = {
  ADD_NEW_DOCUMENT(state, payload) {
    if (state.separatedDocuments.length === 0) {
      state.separatedDocuments.push(payload);
    } else {
      const documents = cloneDeep(state.separatedDocuments);
      state.separatedDocuments = [
        ...documents.slice(0, documents.length - 1),
        payload,
        ...documents.slice(state.separatedDocuments.length - 1),
      ];
    }
  },
  ADD_INITIAL_DOCUMENT(state, payload) {
    state.separatedDocuments = [payload];
    state.numberOfPages = payload.last;
  },
  DELETE_ALL_DOCUMENTS(state) {
    state.separatedDocuments = [];
  },
  DELETE_DOCUMENT(state, index) {
    state.separatedDocuments.splice(index, 1);
  },
  UPDATE_INITIAL_DOCUMENT(state, lastPage) {
    const lastElement = state.separatedDocuments.length - 1;
    const doc = state.separatedDocuments[lastElement];
    doc.first = lastPage + 1;
    doc.last = state.numberOfPages;
  },
  SET_ATTACHMENT_LIST(state, attachments) {
    state.attachmentList = attachments;
  },
  SET_SELECTED_ATTACHMENT(state, attachment) {
    state.splittedAttachment = attachment;
  },
  SORT_DOCUMENTS(state) {
    const documents = state.separatedDocuments;
    documents.sort((a, b) => a.last - b.last);
    state.separatedDocuments = documents;
  },
  SET_PAGINATION(state, payload) {
    state.pagination_splitting = payload;
  },
};

const actions = {
  addNewDocument({ commit }, payload) {
    commit('ADD_NEW_DOCUMENT', payload);
    commit('UPDATE_INITIAL_DOCUMENT', payload.last);
    commit('SORT_DOCUMENTS');
  },
  addInitialDocument({ commit }, payload) {
    commit('ADD_INITIAL_DOCUMENT', payload);
    commit('SORT_DOCUMENTS');
  },
  deleteAllDocuments({ commit }) {
    commit('DELETE_ALL_DOCUMENTS');
  },
  deleteDocumentAtIndex({ commit }, index) {
    commit('DELETE_DOCUMENT', index);
    commit('SORT_DOCUMENTS');
  },
  async createPdfObjects({ state }, { pdfFile }) {
    const pdfObjects = [];
    const { data } = await downloadFile(pdfFile);
    const originalPdf = await PDFDocument.load(data, { ignoreEncryption: true });
    if (state.separatedDocuments.length === 1) {
      return [originalPdf];
    }
    const uniqueDocuments = uniqWith(state.separatedDocuments, isEqual);
    for (const separatedDocument of uniqueDocuments) {
      const pdfDocument = await createPdfFromPages(originalPdf, separatedDocument);
      pdfObjects.push(pdfDocument);
    }
    return pdfObjects;
  },
  async getUnprocessedAttachmentList({ commit }, url) {
    try {
      const { data } = await getUnsplitAttachments(url);
      commit('SET_ATTACHMENT_LIST', data.results);
      commit('SET_PAGINATION', {
        count: data.count,
        current: url,
        next: data.next,
        previous: data.previous,
      });
    } catch (err) {
      throw err;
    }
  },
  async selectAttachment({ commit }, attachment) {
    if (!attachment) {
      return;
    }
    try {
      const { data } = await getAttachmentByUrl(attachment.url);
      commit('SET_SELECTED_ATTACHMENT', data);
    } catch (err) {
      throw err;
    }
  },
  async mergeDocuments(_, pdfDocumentsBytes) {
    const mergedDocument = await PDFDocument.create();
    for (const pdfBytes of pdfDocumentsBytes) {
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      const indices = Array(Math.abs(pdfDoc.getPageCount()))
        .fill()
        .map((item, index) => index);
      const pdfPages = await mergedDocument.copyPages(pdfDoc, indices);
      for (const page of pdfPages) {
        await mergedDocument.addPage(page);
      }
    }
    return mergedDocument;
  },
};

async function createPdfFromPages(pdfDocument, { first, last }) {
  const indicesArray = Array(Math.abs(last - first + 1))
    .fill()
    .map((item, index) => first - 1 + index);
  const newPdfDocument = await PDFDocument.create();
  const pdfPages = await newPdfDocument.copyPages(pdfDocument, indicesArray);
  for (const page of pdfPages) {
    newPdfDocument.addPage(page);
  }
  return newPdfDocument;
}
export default {
  state,
  getters,
  mutations,
  actions,
};
